import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import lamboRear from "@/assets/lambo-rear.jpg";
import paganiRear from "@/assets/pagani-rear.jpg";
import bugattiGreenFront from "@/assets/gallery/bugatti-green-front.jpg";
import koenigseggGumball from "@/assets/gallery/koenigsegg-gumball.jpg";
import porscheGt3Shop from "@/assets/gallery/porsche-gt3-shop.jpg";
import mclarenGreen from "@/assets/gallery/mclaren-green.jpg";
import paganiUtopiaPpf from "@/assets/gallery/pagani-utopia-ppf.jpg.asset.json";
import paganiHuayraRoller from "@/assets/gallery/pagani-huayra-roller.jpg.asset.json";
import koenigseggDoncicWrap from "@/assets/gallery/koenigsegg-doncic-wrap.jpg.asset.json";
import { Button } from "@/components/ui/button";

const images = [{
  src: bugattiGreenFront,
  alt: "Green Bugatti Chiron with PPF protection at Hyper Haus Los Angeles",
  label: "Bugatti Chiron",
  value: "~$4.3M",
  service: "Full Body PPF",
  date: "Aug 2024",
}, {
  src: paganiUtopiaPpf.url,
  alt: "Pagani Utopia receiving full body paint protection film installation at Hyper Haus",
  label: "Pagani Utopia",
  value: "~$5M",
  service: "Full Body PPF",
  date: "Aug 2026",
}, {
  src: paganiHuayraRoller.url,
  alt: "Pagani Huayra rolling shot with full body PPF by Hyper Haus",
  label: "Pagani Huayra",
  value: "~$3.5M",
  service: "Full Body PPF",
  date: "Nov 2025",
}, {
  src: koenigseggDoncicWrap.url,
  alt: "Koenigsegg Regera in a custom printed event wrap by Hyper Haus",
  label: "Koenigsegg Regera",
  value: "~$3.5M",
  service: "Custom Printed Wrap",
  date: "March 2026",
}, {
  src: lamboRear,
  alt: "Lamborghini Diablo GT rear view — full body PPF by Hyper Haus",
  label: "Lamborghini Diablo GT",
  value: "~$1.2M",
  service: "Full Body PPF",
  date: "Mar 2025",
}, {
  src: porscheGt3Shop,
  alt: "Porsche GT3 RS in the Hyper Haus shop receiving PPF installation",
  label: "Porsche GT3 RS",
  value: "~$400K",
  service: "Full Body PPF",
  date: "",
}, {
  src: koenigseggGumball,
  alt: "Koenigsegg Regera Gumball 3000 edition serviced at Hyper Haus LA",
  label: "Koenigsegg Regera",
  value: "~$3.5M",
  service: "Full Body PPF",
  date: "Sep 2025",
}, {
  src: paganiRear,
  alt: "Pagani Huayra R rear — full body PPF by Hyper Haus",
  label: "Pagani Huayra R",
  value: "~$4M",
  service: "Full Body PPF",
  date: "2024",
}, {
  src: mclarenGreen,
  alt: "Green McLaren 650S with color PPF by Hyper Haus",
  label: "McLaren 650S",
  value: "~$150K",
  service: "Color PPF",
  date: "April 2025",
}];



const Gallery = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <section className="py-24 bg-card">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] mb-4">Our Work</p>
          <h2 className="section-heading mb-4">Gallery</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <motion.div
              key={image.alt}
              data-expanded={expandedIndex === index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="aspect-[4/3] overflow-hidden rounded-lg relative group cursor-pointer"
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                width={400}
                height={300}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3 pt-10">
                <p className="text-sm font-semibold text-white drop-shadow-md">
                  {image.label}
                  {image.value && (
                    <span className="transition-opacity duration-300 max-md:hidden group-data-[expanded=true]:max-md:inline text-white/80 font-normal">
                      {" "}· {image.value}
                    </span>
                  )}
                </p>
                <p className="text-xs text-white/90 uppercase tracking-wider font-medium drop-shadow-md">
                  {image.service}
                  {image.date && (
                    <span className="transition-opacity duration-300 max-md:hidden group-data-[expanded=true]:max-md:inline text-white/70">
                      {" "}· {image.date}
                    </span>
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <a
            href="https://www.instagram.com/hyper.haus"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="lg" className="gap-2 border-[#E1306C]/30 text-[#E1306C] hover:bg-[#E1306C]/10 hover:text-[#E1306C] hover:border-[#E1306C]/50">
              <Instagram className="w-5 h-5" />
              See More on Instagram
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Gallery;